import { sumMeasured } from "./derive";
import { MailboxHealthCard } from "./MailboxAPI";

/**
 * A mailbox's standing, worst first.
 *
 * The order of this union IS the sort order — see STANDING and
 * compareMailboxes. Adding a value means deciding where it ranks.
 *
 * `waiting` deliberately echoes existing feature vocabulary rather than
 * coining a new word: TenantTable's "Waiting" column group (headed by
 * Backlog) and BacklogChart.tsx's "Waiting" series. By name alone `waiting`
 * and `stalled` read as near-synonyms in English; that precedent is what
 * justifies both existing as distinct standings here.
 *
 * `neverRun` sits between `waiting` and `healthy`: not a fault (nothing has
 * failed), but ranked above `healthy` because an ENABLED mailbox with no run
 * data is unexplained — freshly added, or the collector is not picking it
 * up — which is worth an operator's eye, unlike a healthy mailbox. See
 * `mailboxStanding` for why a disabled mailbox never reaches it.
 */
export type MailboxStanding =
    "connectFailed" | "runFailed" | "stalled" | "waiting" | "neverRun" | "healthy" | "disabled";

/**
 * Rank for display order, and whether the standing is something an operator
 * should act on.
 *
 * One table rather than two so adding a standing forces BOTH decisions in the
 * same edit. `Record<MailboxStanding, ...>` makes that compiler-enforced: a
 * eighth standing will not compile until it is ranked and classified. An
 * `isFault` written as an `===` chain would instead have compiled clean and
 * quietly reported the new standing as harmless.
 */
const STANDING: Record<MailboxStanding, { rank: number; fault: boolean }> = {
    connectFailed: { rank: 0, fault: true },
    runFailed: { rank: 1, fault: true },
    stalled: { rank: 2, fault: true },
    waiting: { rank: 3, fault: false },
    neverRun: { rank: 4, fault: false },
    healthy: { rank: 5, fault: false },
    disabled: { rank: 6, fault: false },
};

/**
 * Whether this standing is something an operator should act on.
 *
 * `waiting` is not: a measured backlog is the system working, and absolute
 * backlog thresholds are deliberately out of scope until real data shows what
 * normal looks like (phase 1 spec §1).
 *
 * `disabled` is not: it is a configuration statement. The same rule mutes the
 * Core view's Disabled tile.
 */
export function isFault(standing: MailboxStanding): boolean {
    return STANDING[standing].fault;
}

/**
 * Classifies one mailbox.
 *
 * CHECK ORDER MATTERS, and the first check is the one that is easy to get
 * wrong. A mailbox switched off in configuration keeps whatever `lastRun` was
 * stored when it was last switched on — quite possibly a failure. Testing
 * outcomes before enablement would therefore mark a deliberately-disabled
 * mailbox as a fault forever. So enablement is decided first.
 *
 * `LockTimeout` is deliberately absent from the failure checks: it means
 * another collector node held the lock, so this run measured nothing
 * (contract §2.5). It is not a failure and falls through to the checks below,
 * which is why lock contention does not paint the page red.
 */
export function mailboxStanding(card: MailboxHealthCard): MailboxStanding {
    if (card.enablement.inbound === "Disabled" && card.enablement.outbound === "Disabled") {
        return "disabled";
    }

    // TWO-PART HANDLING OF AN ABSENT `lastRun`, on purpose — see
    // MailboxAPI.ts for why absence is itself informative here, so it cannot
    // be skipped with `?.` and left to fall through. Part one, right below:
    // the outcome checks only run `if (card.lastRun)`, so a never-run
    // mailbox cannot fail a check it has no outcome to fail. Part two, after
    // `stalled` and `waiting` — which are independent of `lastRun` and must
    // still win if the server computed them on a never-run mailbox — the
    // explicit `!card.lastRun` check below produces "neverRun" rather than
    // falling through to "healthy".
    if (card.lastRun) {
        const outcomes = [card.lastRun.receive.outcome, card.lastRun.send.outcome];

        if (outcomes.includes("ConnectFailed")) {
            return "connectFailed";
        }
        if (outcomes.includes("Failed")) {
            return "runFailed";
        }
    }

    // Server-computed. Never re-derived from a timestamp (contract §2.3).
    if (card.stalled) {
        return "stalled";
    }
    // An unreported backlog is not zero — but it is also not evidence of mail
    // waiting, so it cannot promote the mailbox to "waiting" on the strength
    // of a measurement nobody made.
    if (card.lastBacklog !== undefined && card.lastBacklog > 0) {
        return "waiting";
    }

    // Not reached by a disabled mailbox — that returns above, because a
    // switched-off mailbox having no run data is expected rather than
    // notable. What lands here is an ENABLED mailbox the server has no run
    // for, which is unexplained: freshly added, or the collector is not
    // picking it up. Ranked above healthy for that reason.
    //
    // Deliberately not a fall-through to "healthy". `lastRun` is absent on
    // most real cards, and treating "nothing is known" as "nothing is wrong"
    // is the defect this feature guards against everywhere else.
    if (!card.lastRun) {
        return "neverRun";
    }
    return "healthy";
}

/**
 * A mailbox's tie-break label.
 *
 * `name` first because it is what the row displays; `address` next because a
 * nameless mailbox still has to sort somewhere sensible. Both absent falls
 * through to `mailboxID` in the comparator, which always exists.
 *
 * A BLANK string counts as absent, not as a label. `??` alone would not do
 * that — it falls through only on null/undefined, so a server-supplied
 * `name: ""` would be treated as a present label and, being shorter than
 * every real name, would sort to the front of its standing group. That is the
 * exact outcome this fallback exists to prevent, so blankness is tested
 * explicitly rather than left to `??`.
 */
function tieBreakLabel(card: MailboxHealthCard): string | undefined {
    const name = card.name?.trim();
    if (name) {
        return name;
    }
    const address = card.address?.trim();
    return address ? address : undefined;
}

/**
 * Orders mailboxes worst-first for display.
 *
 * DELIBERATELY THE OPPOSITE of phase 1's rule for the tenant roster, where
 * the server ranks rows and the client must not re-sort. This endpoint
 * documents no order, so not sorting would leave the order arbitrary rather
 * than leave a server decision intact.
 *
 * Total and stable: every pair has a defined order, and ties break on label
 * then `mailboxID`, so two Refresh clicks cannot reshuffle the list. That
 * matters because comparing the list before and after a fix is the task this
 * view exists for.
 */
export function compareMailboxes(a: MailboxHealthCard, b: MailboxHealthCard): number {
    // Reads the private STANDING table directly. There is no accessor and none
    // is wanted: the table lives in this same file, so exporting a
    // `standingRank()` wrapper would add public surface for an in-file caller.
    // attentionLines.ts does the identical thing with ATTENTION_ORDER.
    const byStanding = STANDING[mailboxStanding(a)].rank - STANDING[mailboxStanding(b)].rank;
    if (byStanding !== 0) {
        return byStanding;
    }

    const labelA = tieBreakLabel(a);
    const labelB = tieBreakLabel(b);
    if (labelA !== undefined && labelB !== undefined) {
        const byLabel = labelA.localeCompare(labelB);
        if (byLabel !== 0) {
            return byLabel;
        }
    } else if ((labelA === undefined) !== (labelB === undefined)) {
        // Exactly one is labelled. The labelled one sorts first: a row with
        // nothing to show for itself belongs at the bottom of its group.
        return labelA === undefined ? 1 : -1;
    }

    return a.mailboxID - b.mailboxID;
}

export interface BacklogCoverage {
    /**
     * Sum over the receiving mailboxes that reported a backlog.
     *
     * ABSENT, not zero, when none of them did. A total of `0` means "every
     * receiving mailbox reported, and they are all empty" — a completely
     * different statement from "nobody reported".
     *
     * No consumer yet: task 8's coverage sentence needs only the ratio. Kept
     * because "measured on 1 of 3" is a claim about a number, and this is that
     * number — the sum the partial figure actually represents.
     */
    total?: number;
    /** How many receiving mailboxes reported a backlog. The numerator. */
    measured: number;
    /** How many mailboxes have receive switched on at all. The denominator. */
    receiving: number;
}

/**
 * How much of this tenant's backlog was actually measured.
 *
 * This is the one thing this view can do that nothing else in the feature
 * can. The Core triage row's `mailboxes.backlog` comes from a server-side
 * `SumOfKnown`, which sums only the mailboxes whose backlog is known — so it
 * can be a confident number quietly measuring less than it claims to, with
 * nothing on the Core wire to detect it from (confirmed with the backend
 * team, 2026-09-03). Here the per-mailbox values are visible, so the
 * partiality can be named.
 *
 * The denominator counts mailboxes with receive switched ON, not every
 * mailbox. A send-only mailbox has no backlog to be missing, so including it
 * would permanently understate coverage and make a fully-measured tenant look
 * partial. This also matches the backend's own reasoning, which sums over
 * switched-on mailboxes.
 *
 * `lastBacklog: 0` counts as measured. That is the whole absent-versus-zero
 * distinction, at the one field on this endpoint that carries it.
 *
 * `receiving: 0` is deliberately NOT partial. A tenant with no receiving
 * mailboxes has no coverage question, so `measured === receiving` answers
 * "complete" there — correctly. Callers need no separate zero check; `total`
 * being absent is what distinguishes it from a fully-measured empty tenant.
 */
export function backlogCoverage(cards: readonly MailboxHealthCard[]): BacklogCoverage {
    const receiving = cards.filter(card => card.enablement.inbound !== "Disabled");
    // Reuses derive.ts's sumMeasured, which already skips absent values and
    // reports how many contributed — the identical shape this needs, and
    // already tested there.
    const summed = sumMeasured(receiving.map(card => card.lastBacklog));

    return {
        total: summed.total,
        measured: summed.measured,
        receiving: receiving.length,
    };
}

export interface GivenUp {
    /** Inbound: dead-lettered only. There is no inbound `abandoned`. */
    inbound: number;
    /** Outbound: dead-lettered PLUS abandoned. */
    outbound: number;
}

/**
 * How much mail has stopped moving without a human, per direction.
 *
 * The merge is phase 1's, for phase 1's reason: `Given up` groups by OPERATOR
 * ACTION rather than by status name, because dead-lettered and abandoned both
 * mean nothing moves without intervention. `derive.ts`'s `rowGivenUp` makes
 * the identical merge across a whole tenant.
 *
 * With one condition, which `rowGivenUp`'s own comment sets: it merges in the
 * overview because "that distinction is needed on the tenant mailbox view
 * where the operator acts, not in this overview." THIS IS THAT VIEW, so the
 * merge here is only half the answer — the remedies differ (contract §3.4:
 * Retry re-arms a send counter on Abandoned, queues a rebuild on
 * DeadLettered), and MailboxRow's expander splits them for exactly that
 * reason. Merging in the column WITHOUT that breakdown would make
 * `rowGivenUp`'s comment false without editing it.
 *
 * The two directions are asymmetric and that is the WIRE's asymmetry, not a
 * gap here: `abandoned` is an outbound-only state — a send counter exhausted —
 * with nothing inbound to correspond to it. So outbound sums two fields while
 * inbound passes one through. Do not "tidy" that into a single expression;
 * the reason it looks lopsided is that the domain is.
 */
export function givenUp(card: MailboxHealthCard): GivenUp {
    return {
        inbound: card.counts.inbound.deadLettered,
        outbound: card.counts.outbound.deadLettered + card.counts.outbound.abandoned,
    };
}
